'use client';

import { api, useQuery } from '@/lib/convex';
import { useDebounce } from '@/hooks/use-debounce';
import { useMemo } from 'react';
import { useLinkScopeSearch } from './use-link-scope-search';
import { useConnectMessageSearch } from './use-connect-message-search';
import { useScheduleSearch } from './use-schedule-search';
import { isLinkScope } from '../types';
import type {
  AestheticResult,
  AvatarResult,
  MemberResult,
  MenuPage,
  OrgResult,
  OrgThreadResult,
  ProductResult,
  ProjectResult,
  SearchScope,
} from '../types';

const ROOT_SEARCH_LIMIT = 10;
const ROOT_RECENT_LIMIT = 5;
const SCOPE_LIMIT = 15;

type DefaultProjectRow = {
  _id: ProjectResult['_id'];
  name: string;
  slug?: string;
  updatedAt?: number;
  _creationTime: number;
  workspacePath?: string;
};

/**
 * All Convex reads for the command menu, gated per page with the skip
 * pattern so only the queries the current page needs are live.
 */
export function useEntitySearch({
  page,
  input,
  orgSlug,
  isAuthenticated: authenticated,
  enabled,
}: {
  page: MenuPage;
  input: string;
  orgSlug?: string;
  isAuthenticated: boolean;
  enabled: boolean;
}) {
  const isAuthenticated = enabled && authenticated;
  const term = input.trim();
  const searchTerm = useDebounce(term, 300).trim();
  const shouldSearch = searchTerm.length >= 2;

  const onRoot = page.kind === 'root';
  const productsActive =
    onRoot || (page.kind === 'scope' && page.scope === 'products');
  const threadsActive =
    (onRoot || (page.kind === 'scope' && page.scope === 'threads')) &&
    Boolean(orgSlug);
  const searchLimit = onRoot ? ROOT_SEARCH_LIMIT : SCOPE_LIMIT;
  const recentLimit = onRoot ? ROOT_RECENT_LIMIT : SCOPE_LIMIT;

  // Products — org-scoped when inside a workspace, personal otherwise.
  const shouldSearchProducts =
    productsActive && shouldSearch && isAuthenticated;

  const orgProductsSearchArgs = useMemo(() => {
    if (!shouldSearchProducts || !orgSlug) return 'skip' as const;
    return { orgSlug, searchTerm, limit: searchLimit };
  }, [shouldSearchProducts, orgSlug, searchTerm, searchLimit]);

  const myProductsSearchArgs = useMemo(() => {
    if (!shouldSearchProducts || orgSlug) return 'skip' as const;
    return { searchTerm, limit: searchLimit };
  }, [shouldSearchProducts, orgSlug, searchTerm, searchLimit]);

  const recentOrgProductsArgs = useMemo(() => {
    if (!productsActive || shouldSearch || !orgSlug || !isAuthenticated) {
      return 'skip' as const;
    }
    return { orgSlug, limit: recentLimit };
  }, [productsActive, shouldSearch, orgSlug, isAuthenticated, recentLimit]);

  const recentMyProductsArgs = useMemo(() => {
    if (!productsActive || shouldSearch || orgSlug || !isAuthenticated) {
      return 'skip' as const;
    }
    return { limit: recentLimit };
  }, [productsActive, shouldSearch, orgSlug, isAuthenticated, recentLimit]);

  const { data: searchedOrgProducts } = useQuery(
    api.products.queries.searchOrgProducts,
    orgProductsSearchArgs
  );
  const { data: searchedMyProducts } = useQuery(
    api.products.queries.searchProducts,
    myProductsSearchArgs
  );
  const { data: recentOrgProducts } = useQuery(
    api.products.queries.getOrgProducts,
    recentOrgProductsArgs
  );
  const { data: recentMyProducts } = useQuery(
    api.products.queries.getMyProducts,
    recentMyProductsArgs
  );

  // Threads — org-scoped only.
  const threadsSearchArgs = useMemo(() => {
    if (!threadsActive || !shouldSearch || !orgSlug || !isAuthenticated) {
      return 'skip' as const;
    }
    return { orgSlug, searchTerm, limit: searchLimit };
  }, [
    threadsActive,
    shouldSearch,
    orgSlug,
    isAuthenticated,
    searchTerm,
    searchLimit,
  ]);

  const recentThreadsArgs = useMemo(() => {
    if (!threadsActive || shouldSearch || !orgSlug || !isAuthenticated) {
      return 'skip' as const;
    }
    return { orgSlug, limit: recentLimit };
  }, [threadsActive, shouldSearch, orgSlug, isAuthenticated, recentLimit]);

  const { data: searchedThreads } = useQuery(
    api.organizations.threads.queries.searchThreads,
    threadsSearchArgs
  );
  const { data: recentThreads } = useQuery(
    api.organizations.threads.queries.listRecentThreads,
    recentThreadsArgs
  );

  // Workspaces — also powers the navigability filter for personal products.
  const { data: organizations } = useQuery(
    api.organizations.queries.list,
    !isAuthenticated ? 'skip' : {}
  );

  const productResults: ProductResult[] = useMemo(() => {
    if (!productsActive) return [];
    if (shouldSearch) {
      return (orgSlug ? searchedOrgProducts : searchedMyProducts) || [];
    }
    return (orgSlug ? recentOrgProducts : recentMyProducts) || [];
  }, [
    productsActive,
    shouldSearch,
    orgSlug,
    searchedOrgProducts,
    searchedMyProducts,
    recentOrgProducts,
    recentMyProducts,
  ]);

  const threadResults: OrgThreadResult[] = useMemo(() => {
    if (!threadsActive) return [];
    // listRecentThreads and searchThreads already project this shape server-side.
    if (shouldSearch) return searchedThreads || [];
    return recentThreads || [];
  }, [threadsActive, shouldSearch, searchedThreads, recentThreads]);

  const orgResults: OrgResult[] = useMemo(() => {
    if (!organizations || !onRoot) return [];
    if (!term) return organizations.slice(0, 5);
    const lower = term.toLowerCase();
    return organizations
      .filter(
        (org) =>
          org.name.toLowerCase().includes(lower) ||
          org.slug.toLowerCase().includes(lower)
      )
      .slice(0, 5);
  }, [organizations, term, onRoot]);

  const organizationSlugById = useMemo(() => {
    const slugById = new Map<string, string>();
    for (const organization of organizations ?? []) {
      slugById.set(String(organization._id), organization.slug);
    }
    return slugById;
  }, [organizations]);

  // Expanded scopes — searched at root fan-out, or on their own scope page.
  const onScope = (scope: SearchScope) =>
    page.kind === 'scope' && page.scope === scope;
  const fanOutLimit = onRoot ? ROOT_SEARCH_LIMIT / 2 : SCOPE_LIMIT;
  const scopeSearchArgs = (scope: SearchScope) => {
    const active = (onRoot && shouldSearch) || (onScope(scope) && shouldSearch);
    if (!active || !orgSlug || !isAuthenticated) return 'skip' as const;
    return { orgSlug, searchTerm, limit: fanOutLimit };
  };

  const projectsArgs = scopeSearchArgs('projects');
  const { data: searchedProjects } = useQuery(
    api.organizations.threads.folders.searchProjects,
    projectsArgs
  );
  // Projects scope page with no term falls back to the visible project list.
  const defaultProjectsArgs =
    onScope('projects') && !shouldSearch && orgSlug && isAuthenticated
      ? { orgSlug }
      : ('skip' as const);
  const { data: defaultProjects } = useQuery(
    api.organizations.threads.folders.listProjects,
    defaultProjectsArgs
  );
  const avatarsArgs = scopeSearchArgs('avatars');
  const { data: searchedAvatars } = useQuery(
    api.avatars.queries.searchOrgAvatars,
    avatarsArgs
  );
  const aestheticsArgs = scopeSearchArgs('aesthetics');
  const { data: searchedAesthetics } = useQuery(
    api.aesthetics.queries.searchAestheticsForOrg,
    aestheticsArgs
  );
  // Members: empty term returns recent members — the scope page default.
  const membersActive = (onRoot && shouldSearch) || onScope('members');
  const membersArgs =
    membersActive && orgSlug && isAuthenticated
      ? { orgSlug, searchTerm, limit: fanOutLimit }
      : ('skip' as const);
  const { data: searchedMembers } = useQuery(
    api.organizations.queries.searchOrgMembers,
    membersArgs
  );

  const projects: ProjectResult[] = useMemo(() => {
    if (onScope('projects') && !shouldSearch) {
      return (defaultProjects ?? [])
        .slice(0, SCOPE_LIMIT)
        .map((project: DefaultProjectRow) => ({
          _id: project._id,
          name: project.name,
          slug: project.slug,
          updatedAt: project.updatedAt ?? project._creationTime,
          workspacePath: project.workspacePath,
        }));
    }
    return searchedProjects ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, shouldSearch, defaultProjects, searchedProjects]);

  const products = useMemo(() => {
    if (orgSlug) return productResults;
    return productResults.filter((product) => {
      // Product detail routes are workspace-scoped, so legacy no-org products
      // are not navigable from the command menu.
      if (!product.organizationId) return false;
      return organizationSlugById.has(String(product.organizationId));
    });
  }, [orgSlug, productResults, organizationSlugById]);

  const threads = useMemo(
    () => (orgSlug ? threadResults : []),
    [orgSlug, threadResults]
  );

  const avatars: AvatarResult[] = useMemo(
    () => searchedAvatars ?? [],
    [searchedAvatars]
  );
  const aesthetics: AestheticResult[] = useMemo(
    () => searchedAesthetics ?? [],
    [searchedAesthetics]
  );
  const members: MemberResult[] = useMemo(
    () => searchedMembers ?? [],
    [searchedMembers]
  );

  const { results: schedules, isPending: schedulesPending } = useScheduleSearch(
    {
      active: isAuthenticated && onScope('scheduled'),
      orgSlug,
      term,
    }
  );

  const { results: links, isPending: linksPending } = useLinkScopeSearch({
    scope:
      isAuthenticated && page.kind === 'scope' && isLinkScope(page.scope)
        ? page.scope
        : null,
    orgSlug,
    term: searchTerm,
  });

  // Connect messages on the top level: typing part of a message finds it
  // without entering the chats scope (which keeps its own, longer list).
  const { messages, isPending: messagesPending } = useConnectMessageSearch({
    active: isAuthenticated && onRoot && shouldSearch,
    orgSlug,
    term: searchTerm,
    limit: ROOT_SEARCH_LIMIT / 2,
  });

  // A started query that has not answered yet, or typing the debounce has
  // not caught up with: the menu shows a loader rather than "No results".
  const waiting = (args: unknown, data: unknown) =>
    args !== 'skip' && data === undefined;
  const isPending =
    term !== searchTerm ||
    waiting(orgProductsSearchArgs, searchedOrgProducts) ||
    waiting(myProductsSearchArgs, searchedMyProducts) ||
    waiting(recentOrgProductsArgs, recentOrgProducts) ||
    waiting(recentMyProductsArgs, recentMyProducts) ||
    waiting(threadsSearchArgs, searchedThreads) ||
    waiting(recentThreadsArgs, recentThreads) ||
    waiting(projectsArgs, searchedProjects) ||
    waiting(defaultProjectsArgs, defaultProjects) ||
    waiting(avatarsArgs, searchedAvatars) ||
    waiting(aestheticsArgs, searchedAesthetics) ||
    waiting(membersArgs, searchedMembers) ||
    schedulesPending ||
    linksPending ||
    messagesPending;

  return {
    isPending,
    products,
    threads,
    orgs: orgResults,
    projects,
    avatars,
    aesthetics,
    members,
    schedules,
    links,
    messages,
    organizationSlugById,
    shouldSearch,
    term,
  };
}
